import React, { FC, useCallback, useState, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { OnboardingCompanyDto } from '@sparling/financial-common'
import { TitleOnBoarding } from '../../../../components/TitleOnBoarding'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { setStepperFormData } from '../../../../services/setStepperFormData'
import { CInputField } from '../../../../components/CustomInputs/CInputField'
import { InputFieldPassword } from '../../../../components/CustomInputs/InputFieldPassword'
import { getStepperFormData } from '../../../../services/getStepperFormData'
import useDebounce from '../../../../hooks/useDebounce'
import { useRest } from '../../../../components/Context'

const resolver = classValidatorResolver(OnboardingCompanyDto)

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 1184,
    margin: '0 auto',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 847,
  },
  formContainer: {
    maxWidth: 632,
    paddingTop: theme.spacing(10),
  },
  inputWrap: {
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  select: { width: '100%' },
}))

interface ICreateCompany {
  title: string
}

export const CreateCompany: FC<ICreateCompany> = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [activeStep] = useState(2)
  const [Email, setEmail] = useState<string>('')
  const [isDisabled, setDisabled] = useState<boolean>(false)
  const debouncedValue = useDebounce<string>(Email, 200)
  const { uniqKey } = useParams() as { [key: string]: string }
  const { rxios } = useRest()

  const formDataStorage = getStepperFormData()

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: formDataStorage.company || {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      role: 'admin',
      password: '',
    },
    resolver,
  })

  const { watch /* , trigger */ } = formMethods

  const emailVerification = useCallback(
    (email: string) => {
      rxios
        .post('company/onboarding/email-verification', {
          email,
          hash: uniqKey,
        })
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setDisabled(false)
          },
          (error) => {
            toast.error(error.response.data.message)
            setDisabled(true)
          },
        )
      // eslint-disable-next-line
    },
    [rxios, uniqKey],
  )

  useEffect(() => {
    if (/^[a-zA-Z0-9._-]+@(?:[a-zA-Z0-9-]+\.)+([a-zA-Z0-9-]{2,})+$/.test(Email)) {
      if (Email !== formDataStorage?.company?.email) {
        emailVerification(Email)
      }
    }
    // eslint-disable-next-line
  }, [debouncedValue])

  const { isValid } = formMethods.formState

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'email') setEmail(value[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const onSubmit = useCallback(
    (newValue) => {
      const data = getStepperFormData()
      setStepperFormData({
        ...data,
        company: {
          firstName: newValue.firstName,
          lastName: newValue.lastName,
          companyName: newValue.companyName,
          email: newValue.email,
          role: 'admin',
          password: newValue.password,
        },
      })
      history.push({
        ...location,
        state: {
          activeStep,
        },
      })
    },
    [activeStep, history, location],
  )

  return (
    <main>
      <FormProvider {...formMethods}>
        <Grid item lg className={classes.container}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)} className={classes.form}>
            <TitleOnBoarding title="Create account" activeStep={activeStep} />

            <Grid container item className={classes.formContainer} direction={'column'}>
              <Grid container className={classes.inputWrap}>
                <CInputField name={'firstName'} label={'First Name'} />
                <CInputField name={'lastName'} label={'Last Name'} />
                <CInputField name={'companyName'} label={'Company Name'} />
                <CInputField name={'email'} label={'Company Email'} isCompanyEmail />
                <Grid className={classes.select}>
                  <CInputField name={'role'} label={'Your Role'} defaultValue={'Admin'} disabled />
                </Grid>
                <InputFieldPassword name={'password'} label={'Create Password'} />
                <InputFieldPassword name={'confirmPassword'} label={'Confirm Password'} />
              </Grid>
            </Grid>
            <GreenButton disabled={!isValid || isDisabled} type="submit" width={320} mt={72} text="Next" />
          </form>
        </Grid>
      </FormProvider>
    </main>
  )
}
